"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const videos = [{
  id: "v1",
  title: "Waymo navigerar i San Francisco",
  description: "Följ med på en helt förarlös resa genom de livliga gatorna i San Francisco med Waymos senaste generation av robotaxis under rusningstid.",
  embedUrl: "https://www.youtube-nocookie.com/embed/M7lc1UVf-VE?si=robotaxi1"
}, {
  id: "v2",
  title: "Cruise expanderar i Phoenix",
  description: "En inblick i hur autonoma fordon skalar upp sin verksamhet och erbjuder säkra, dygnet runt-tjänster i Phoenix, Arizona.",
  embedUrl: "https://www.youtube-nocookie.com/embed/M7lc1UVf-VE?si=robotaxi2"
}, {
  id: "v3",
  title: "Framtiden med autonoma fordon i USA",
  description: "En djupdykning i tekniken bakom självkörande bilar och hur de förväntas förändra stadsbilden och mobiliteten över hela USA.",
  embedUrl: "https://www.youtube-nocookie.com/embed/M7lc1UVf-VE?si=robotaxi3"
}];
export default function VideoGalleryRobotaxiInUsa() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="videogalleri-robotaxi-i-usa" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Videogalleri – Robotaxi i USA
          </h2>
          <p className="text-lg text-muted-foreground">
            Upptäck hur autonoma fordon redan idag transformerar transportsektorn
            i amerikanska storstäder. Se tekniken i praktiken.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {videos.map((video, i) => <VideoCard key={video.id} video={video} index={i} />)}
        </div>
      </div>
    </section>;
}
function VideoCard({
  video,
  index
}: {
  video: (typeof videos)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full flex flex-col pt-0 overflow-hidden border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="w-full bg-muted">
          <AspectRatio ratio={16 / 9}>
            <iframe src={video.embedUrl} title={video.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="w-full h-full border-0"></iframe>
          </AspectRatio>
        </div>
        <CardHeader className="flex-grow pt-6">
          <CardTitle className="text-xl leading-tight mb-2">
            {video.title}
          </CardTitle>
          <CardDescription className="text-base">
            {video.description}
          </CardDescription>
        </CardHeader>
      </Card>
    </div>;
}